// Completing some care-team tasks messages the patient (WF17 at once, WF72's lab card
// later), and the send is irreversible. This states the FACTS the operator needs before committing —
// which channels fire, to whom, and what else gets created — deliberately NOT the
// verbatim message (that made the dialog a wall of text nobody read).
//
// Gating mirrors api/workflows-target/17-task-engine-task-update.json
// ('Labs Still Pending?'). Keep the template SET in sync — a WF17 template missing
// here completes with no confirmation at all (pinned by S-78).

import { labsResultOf, type TaskLike } from './labs';

export interface NotifyOnCompleteSpec {
  // 'wf17' texts and emails right now. 'wf72' posts the lab card in the chat and texts with it, at any
  // hour, so there is no channel to resolve and `summary` is the whole sentence.
  sender: 'wf17' | 'wf72';
  // Factual: what the patient is being sent. Not the message itself.
  summary: string;
  // WF17 sends NOTHING when this template's task is already completed.
  suppressIfCompleted?: string;
  // Side effects other than the patient message, for disclosure.
  alsoCreates?: readonly string[];
  // Overrides the generic "Cancel — no longer needed" hand-off label where the reason
  // for NOT sending is specific (a lab order is waived because results were found).
  cancelLabel?: string;
  // The dialog offers the care team's note for the patient (shown only while LAB_NOTE_FIELD_ON).
  noteField?: boolean;
}

// WF17 falls back to 'there' when the patient record carries no first name.
export const NOTIFY_FIRST_NAME_FALLBACK = 'there';

// The lab-results release notice: on since go-live, with PROD's LAB_RESULTS_RELEASE='true'.
export const LAB_RELEASE_NOTICE_ON = true;
// The care team note field (spec 2026-10-14-lab-results-care-team-note): on since go-live, with PROD's LAB_NOTE_RELEASE='true'.
export const LAB_NOTE_FIELD_ON = true;

export const LAB_PARTIAL_NOTE = 'Some results are not in yet: only the finished ones are shared, and the rest will not follow automatically.';
// WF72 shares only what our Quest order holds in the chart by the Done day, so with nothing in, likely nothing goes.
export const LAB_NONE_NOTE = 'No Quest results are in the chart yet. Only results filed by today can be shared, so the patient may get nothing: share their results in the chat.';

export const NOTIFY_ON_COMPLETE: Record<string, NotifyOnCompleteSpec> = {
  onb_baseline_labs_order: {
    sender: 'wf17',
    summary: 'their booking instructions for the blood draw at Quest',
    cancelLabel: 'I found labs in their medical records',
    suppressIfCompleted: 'onb_patient_labs_booking',
    alsoCreates: [
      'Review baseline lab results — clinician, due in 14 days',
      'Book your baseline labs — patient to-do, due in 14 days',
    ],
  },
  onb_baseline_labs_review: {
    sender: 'wf72',
    summary: 'Marking this Done may share the lab results with the patient in their chat and text them.',
    noteField: true,
  },
};

// Only the template match is modelled. WF17's send gate has three further
// conditions — new_status==='completed', old_status!=='completed', and the PATCH
// succeeding — none of which the UI can be wrong about today: the gate is reached
// only from a Complete action on an OPEN task, and the send happens after the
// PATCH. If re-completing a completed task ever becomes reachable, model the
// old_status condition too or the modal will promise a send WF17 suppresses.
export function notifySpecFor(
  templateCode: string | null | undefined,
  { labReleaseNotice = LAB_RELEASE_NOTICE_ON }: { labReleaseNotice?: boolean } = {},
): NotifyOnCompleteSpec | null {
  if (!templateCode) return null;
  // hasOwn is load-bearing, not ceremony: a bare lookup of 'constructor' /
  // 'toString' resolves through the prototype and returns a truthy function.
  if (!Object.hasOwn(NOTIFY_ON_COMPLETE, templateCode)) return null;
  const spec = NOTIFY_ON_COMPLETE[templateCode];
  if (spec?.sender === 'wf72' && !labReleaseNotice) return null;
  return spec ?? null;
}

// What the WF72 notice adds from the task itself: a partial panel, or no results stamped yet.
export function notifyNotesFor(spec: NotifyOnCompleteSpec, task: TaskLike | null | undefined): string[] {
  if (spec.sender !== 'wf72' || !task) return [];
  const state = labsResultOf(task)?.state;
  if (state === 'resulted') return [];
  return [state === 'partial' ? LAB_PARTIAL_NOTE : LAB_NONE_NOTE];
}

export interface NotifyTaskLike { template_code?: string | null; status?: string | null }

export interface NotifyOutcomeInput {
  firstName?: string | null;
  // MUST come from the patients table (the row WF17 reads), never from the
  // Withings demographics fallbacks in logic/patientContact.ts — those would
  // claim a send WF17 will skip.
  phone?: string | null;
  email?: string | null;
  suppressionTasks?: NotifyTaskLike[] | null;
  lookupFailed?: boolean;
}

export interface NotifyOutcome {
  firstName: string;
  suppressed: boolean;
  lookupFailed: boolean;
  willSendSms: boolean;
  willSendEmail: boolean;
  sendsNothing: boolean;
  noContactOnFile: boolean;
  summary: string;
  alsoCreates: readonly string[];
}

export function resolveNotifyOutcome(
  spec: NotifyOnCompleteSpec,
  input: NotifyOutcomeInput = {},
): NotifyOutcome {
  // Bare truthiness, NOT .trim() — WF17 does `patient.first_name || 'there'`,
  // `patient.phone || ''`. Trimming here would claim "won't send" on a
  // whitespace-only phone that WF17 still fires (and logs as failed).
  const firstName = input.firstName || NOTIFY_FIRST_NAME_FALLBACK;
  const phone = input.phone || '';
  const email = input.email || '';

  // Unknown suppression state is NOT suppression — WF17 may still send, so the
  // modal must warn rather than promise silence.
  const lookupFailed = !!input.lookupFailed;
  // FIRST matching row, mirroring WF17's `rows.find(...)` over an unsorted
  // limit=5 GET — a `.some()` would disagree whenever the patient has several
  // booking rows (e.g. a cancelled one from a prior close-cascade + a completed
  // one), and promise silence while WF17 sends.
  const booking = spec.suppressIfCompleted
    ? (input.suppressionTasks || []).find((t) => t?.template_code === spec.suppressIfCompleted)
    : undefined;
  const suppressed = !lookupFailed && !!booking && booking.status === 'completed';

  const willSendSms = !suppressed && !!phone;
  const willSendEmail = !suppressed && !!email;

  return {
    firstName,
    suppressed,
    lookupFailed,
    willSendSms,
    willSendEmail,
    sendsNothing: !willSendSms && !willSendEmail,
    noContactOnFile: !suppressed && !phone && !email,
    summary: spec.summary,
    alsoCreates: spec.alsoCreates ?? [],
  };
}
